import React, { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import type { AxiosError } from "axios"
import { getRequest } from "../../../helpers"

type RouteParams = { token?: string }
type AcceptInviteResponse = {
  status: string
  redirectUrl?: string | null
}

const ERROR_STATUS = ["invite_not_found", "invalid_status", "invite_conflict", "invalid_token", "error"]

const JoinTeamContainer: React.FC = () => {
  const { t } = useTranslation()
  const { token } = useParams<RouteParams>()

  const [loading, setLoading] = useState<boolean>(true)
  const [response, setResponse] = useState<AcceptInviteResponse | null>(null)

  useEffect(() => {
    let mounted = true

    const callAcceptInvite = async (): Promise<void> => {
      try {
        const response = await getRequest({
          url: `/api/v1/profile/team/accept-invite/${encodeURIComponent(token ?? "")}`
        })
        if (!mounted) return
        setResponse(response as AcceptInviteResponse)
      } catch (error: unknown) {
        if (!mounted) return
        const axiosError = error as AxiosError<AcceptInviteResponse | { message?: string }>
        const api = axiosError?.response?.data as AcceptInviteResponse | undefined
        setResponse(api ?? { status: "error", redirectUrl: null })
      }
      setLoading(false)
    }

    if (token) {
      void callAcceptInvite()
    } else {
      setResponse({ status: "invalid_token", redirectUrl: null })
      setLoading(false)
    }

    return () => {
      mounted = false
    }
  }, [token])

  const hasError = useMemo(() => {
    if (!response) return false
    return ERROR_STATUS.includes(response.status)
  }, [response])

  useEffect(() => {
    if (!loading && response && !hasError) {
      const target = response.redirectUrl || "/login"

      setTimeout(() => {
        window.location.href = target
      }, 5000)
    }
  }, [loading, response, hasError])

  if (loading) {
    return (
      <div className="tw-mx-auto tw-mt-12 tw-max-w-xl">
        <div className="tw-rounded-2xl tw-border tw-border-dark-15 tw-bg-white tw-p-6 tw-shadow-sm">
          <h1 className="tw-mb-2 tw-text-xl tw-font-semibold">{t("auth.join.loadingTitle", "Accepting invite")}</h1>
          <p className="tw-text-sm tw-text-dark-75">
            {t("auth.join.loadingText", "Please wait while we process your invitation.")}
          </p>
        </div>
      </div>
    )
  }

  const statusKey = response?.status ? `auth.join.status.${response.status}` : "auth.join.status.error"
  return (
    <div className="tw-mx-auto tw-mt-12 tw-max-w-xl">
      <div
        // TODO: use twClassNames()
        className={
          hasError
            ? "tw-rounded-2xl tw-border tw-border-red-200 tw-bg-red-50 tw-p-6 tw-shadow-sm"
            : "tw-rounded-2xl tw-border tw-border-emerald-200 tw-bg-emerald-50 tw-p-6 tw-shadow-sm"
        }>
        <h1
          className={
            hasError
              ? "tw-mb-2 tw-text-xl tw-font-semibold tw-text-red-800"
              : "tw-mb-2 tw-text-2xl tw-font-semibold tw-text-emerald-800"
          }>
          {hasError
            ? t("auth.join.errorTitle", "Failed to accept invitation")
            : t("auth.join.successTitle", "Invitation accepted")}
        </h1>
        <p className={hasError ? "tw-mb-6 tw-text-sm tw-text-red-900" : "tw-mb-6 tw-text-sm tw-text-emerald-900"}>
          {t(statusKey)}
        </p>
        <div className="tw-flex tw-gap-3">
          <button
            className={hasError ? "tw-btn tw-btn-outline" : "tw-btn tw-btn-primary"}
            onClick={() => {
              const target = response?.redirectUrl || "/login"
              window.location.href = target
            }}>
            {hasError ? t("auth.join.backToLogin", "Back to login") : t("auth.join.continue", "Continue")}
          </button>
        </div>
      </div>
    </div>
  )
}

export default JoinTeamContainer
